import { useEffect, useRef } from 'react';

import { ContentDeliverable, License, LicenseReturnable, Transaction, TransactionApi, TransactionSortField } from 'apis/TransactionApi';
import { TransactionGMetrixTable } from 'components/TransactionGMetrixTable/TransactionGMetrixTable';
import { getLowestLicenseAmount, getMaxLicenseAmount, unlimitedUses } from 'utils/objects';
import { ConditionalWrapper } from 'components/conditional_wrapper/ConditionalWrapper';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import NewAccessCodeForm from 'components/NewAccessCodeForm/NewAccessCodeForm';
import { duplicateTransaction } from 'pages/access_codes/duplicateTransaction';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import { FindContent } from 'components/FindContent/FindContent';
import { useAccessCodes } from 'pages/access_codes/context';
import { copyToClipboard, getFormattedDate } from 'utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { helpContent } from 'pages/access_codes/help';
import { useSuperState, useUrlFilters } from 'hooks';
import { useNotification } from 'app/notifications';
import { partnerTypeNames } from 'apis/PartnerApi';
import { PageWithHeader } from 'components/layout';
import { Card, Modal, Row } from 'react-bootstrap';
import { SortDescriptor } from 'utils/dataQuery';
import { KendoIntlProvider } from 'app/culture';
import { currentLookupUrl } from 'utils/other';
import { useNavigate } from 'react-router-dom';
import { UsersApi } from 'apis/UsersApi';
import { useJoyride } from 'app/joyride';
import { TransactionType } from 'types';
import { useApp } from 'app';

import 'pages/access_codes/styles/main.scss';
import 'styles/global-styles.scss';
import 'styles/kendo-default-theme-with-overrides.scss';

export const AccessCodes = () => {

    const navigate = useNavigate();

    // Persist the view (filters, search, sort, pagination) in the URL query string so
    // navigating away and back restores the same view, then refetch fresh access-code rows.
    const urlFilters = useUrlFilters({
        archived: false,
        mine: false,
        searchterm: '',
        skip: 0,
        sortdir: '',
        sortfield: '',
        take: 15
    });

    // Hooks run before the state initializer below, so formatMessage is safe to use there.
    const intl = useIntl();
    const { formatMessage, locale } = intl;

    const [state, setState] = useSuperState({

        // Computed synchronously at first render: showAccessCodeHeader derives from this
        // and transactionTitle, and if either settles later the ConditionalWrapper around
        // the table flips, remounting the table — whose mount effect re-emits its filters
        // and fires a duplicate (expensive) GetAllPaged fetch.
        classroomContentTab: window.location === window.parent.location,
        currentAccessCodeStep: 0,
        expiringMessage: '',
        expiringMessageLonger: '',
        filtersInitialized: false,
        globalLicenses: null as LicenseReturnable | null,
        licenseExpire: false,
        licensesExpiring: [] as License[],
        loadingGridData: true,
        newAccessCodeData: [] as ContentDeliverable[] | undefined,
        paginationSkip: urlFilters.initial.skip,
        paginationTake: urlFilters.initial.take,
        searchTerm: urlFilters.initial.searchterm,
        selectedTransactions: [] as Transaction[],
        serverTotalCount: 0,
        showArchived: urlFilters.initial.archived,
        showExpiringLicenseNoticeShow: false,
        showMyAccessCodes: urlFilters.initial.mine,
        showNewAccessCodeModal: false,
        sortDir: urlFilters.initial.sortdir,
        sortField: urlFilters.initial.sortfield as TransactionSortField | '',
        tableKey: 'tableKey',
        transactions: [] as Transaction[],
        transactionTitle: formatMessage({ id: 'access_codes.3114ae04', defaultMessage: 'Access Codes' })
    });

    const { classroomContentTab, currentAccessCodeStep, expiringMessage, expiringMessageLonger, filtersInitialized, globalLicenses,
        licenseExpire, licensesExpiring, loadingGridData, newAccessCodeData, paginationSkip, paginationTake, searchTerm,
        selectedTransactions, serverTotalCount, showArchived, showExpiringLicenseNoticeShow, showMyAccessCodes,
        showNewAccessCodeModal, sortDir, sortField, tableKey, transactions, transactionTitle } = state;

    const { isSystemMode, selectedPartner, userId } = useApp();
    const { permissions } = useAccessCodes();
    const { joyrideTours, loadCurrentTour } = useJoyride();
    const { catchWithToast, createToast } = useNotification();

    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isInitialSearchRef = useRef(true);

    const dateDiff = (dateEarlier: Date, dateLater: Date) => {

        const one_day = 1000 * 60 * 60 * 24;
        return Math.round((dateLater.getTime() - dateEarlier.getTime()) / one_day);

    };

    const checkLicenses = async (licenseObj: LicenseReturnable) => {

        let expireLicenseMessage = '';
        let expireLicenseMessageLonger = '';
        const licensesToWarn: License[] = [];

        licenseObj.licenses.forEach(license => {

            if (getLowestLicenseAmount(license) === unlimitedUses) return;

            if (dateDiff(new Date(), new Date(license.OrderExpireDate)) <= 30) {

                expireLicenseMessage = formatMessage({ id: 'your_license_is_about_to_expire.c278d41f', defaultMessage: 'Your License is about to expire' });
                expireLicenseMessageLonger = formatMessage({ id: 'one_or_more_of_your_licenses_is_about_to.4e0936b4', defaultMessage: 'One or more of your licenses is about to expire. You may want to renew them.' });
                licensesToWarn.push(license);

            } else if (((getLowestLicenseAmount(license) / getMaxLicenseAmount(license)) * 100) <= 10) {

                expireLicenseMessage = formatMessage({ id: 'your_license_is_nearly_out_of_uses.cab5eca3', defaultMessage: 'Your license is nearly out of uses.' });
                expireLicenseMessageLonger = formatMessage({ id: 'one_or_more_of_your_licenses_is_nearly_o.cb1e42a5', defaultMessage: 'One or more of your licenses is nearly out of uses. You may want to renew them.' });
                licensesToWarn.push(license);

            }

        });

        if (licensesToWarn.length) {

            setState({
                expiringMessage: expireLicenseMessage,
                expiringMessageLonger: expireLicenseMessageLonger,
                licensesExpiring: licensesToWarn,
                showExpiringLicenseNoticeShow: true
            });

        }

    };

    // Loads the license data the "New Access Code" button + expiring-license notice depend on.
    const loadGlobalData = async () => {

        const licenseObj = await TransactionApi.GetAllLicenses().catch(catchWithToast(formatMessage({ id: 'failed_to_load_license_data.ccdc93b9', defaultMessage: 'Failed to load license data.' })));

        if (licenseObj) {

            setState({ globalLicenses: licenseObj });
            if (permissions.ExpiringLicense) {

                checkLicenses(licenseObj);

            }

        }

    };

    useEffect(() => {

        loadGlobalData();

    }, []);

    useEffect(() => {

        if (joyrideTours.length > 0) {

            loadCurrentTour('PageLoad');

        }

    }, [joyrideTours]);

    // Debounce search term changes for server-side pagination (1s delay)
    useEffect(() => {

        // The first server call is kicked off by the table filters on mount.
        // Skip the first debounced run to avoid duplicate requests.
        if (isInitialSearchRef.current) {

            isInitialSearchRef.current = false;
            return;

        }

        if (searchDebounceRef.current) {

            clearTimeout(searchDebounceRef.current);

        }

        if (!isSystemMode || searchTerm.trim()) {

            setState({ loadingGridData: true });

        }

        searchDebounceRef.current = setTimeout(() => {

            setState({ paginationSkip: 0 });
            fetchTransactionsPaged(0, paginationTake, sortField, sortDir, searchTerm, showArchived, showMyAccessCodes);

        }, 1000);

        return () => {

            if (searchDebounceRef.current) {

                clearTimeout(searchDebounceRef.current);

            }

        };

    }, [searchTerm]);

    const fetchTransactionsPaged = async (
        skip = paginationSkip,
        take = paginationTake,
        sField = sortField,
        sDir = sortDir,
        search = searchTerm,
        archived = showArchived,
        myAccessCodes = showMyAccessCodes
    ) => {

        // In system mode (no testing center selected) a search term is required before querying
        // across all partners, mirroring the system-level Students view.
        if (isSystemMode && !search.trim()) {

            setState({ loadingGridData: false, serverTotalCount: 0, transactions: [] });
            return;

        }

        setState({ loadingGridData: true });
        const page = Math.floor(skip / take) + 1;

        try {

            const result = await TransactionApi.GetAllPaged(
                page,
                take,
                sField,
                sDir,
                search,
                archived,
                myAccessCodes
            );
            setState({ serverTotalCount: result.TotalCount, transactions: result.Items });
            urlFilters.write({
                archived,
                mine: myAccessCodes,
                searchterm: search,
                skip,
                sortdir: sDir,
                sortfield: sField,
                take
            });

        } catch (e) {

            console.error(e);
            const message = typeof e === 'string' ? e : (e as any)?.message || (e as any)?.Message || '';
            if (message.toLowerCase().includes('timed out') || message.toLowerCase().includes('timeout')) {

                createToast(formatMessage({ id: 'the_request_timed_out_please_try_again.93e77e8d', defaultMessage: 'The request timed out. Please try again.' }), 'danger');

            } else {

                createToast(formatMessage({ id: 'failed_to_load_access_code_data_please_t.868dc9a9', defaultMessage: 'Failed to load access code data. Please try again.' }), 'danger');

            }

        } finally {

            setState({ loadingGridData: false });

        }

    };

    const handleServerPageChange = (skip: number, take: number) => {

        setState({ paginationSkip: skip, paginationTake: take });
        fetchTransactionsPaged(skip, take, sortField, sortDir, searchTerm, showArchived, showMyAccessCodes);

    };

    const handleServerSortChange = (sort: SortDescriptor[]) => {

        const newSortField = (sort.length > 0 ? sort[0].field : '') as TransactionSortField | '';
        const newSortDir = sort.length > 0 ? (sort[0].dir ?? '') : '';
        setState({ paginationSkip: 0, sortDir: newSortDir, sortField: newSortField });
        fetchTransactionsPaged(0, paginationTake, newSortField, newSortDir, searchTerm, showArchived, showMyAccessCodes);

    };

    const handleFilterChange = (filters: { showArchived: boolean; showMyAccessCodes: boolean }) => {

        // The table emits its filters once on mount and again whenever the user toggles one.
        // On the first (mount) emission, restore the previous page/sort/search and refetch the
        // exact view the user left; on later emissions a filter actually changed, so reset to
        // the first page.
        if (!filtersInitialized) {

            setState({ filtersInitialized: true, showArchived: filters.showArchived, showMyAccessCodes: filters.showMyAccessCodes });
            fetchTransactionsPaged(paginationSkip, paginationTake, sortField, sortDir, searchTerm, filters.showArchived, filters.showMyAccessCodes);
            return;

        }

        setState({ paginationSkip: 0, showArchived: filters.showArchived, showMyAccessCodes: filters.showMyAccessCodes });
        fetchTransactionsPaged(0, paginationTake, sortField, sortDir, searchTerm, filters.showArchived, filters.showMyAccessCodes);

    };

    const handleDuplicateTransaction = async (dataItem: Transaction, setDuplicateProcess?: any) => {

        try {

            const newId = await duplicateTransaction(dataItem, intl, createToast);
            if (newId) {

                navigate('/access-codes/' + newId);

            }

        } finally {

            if (setDuplicateProcess) {

                setDuplicateProcess(false);

            }

        }

    };

    const checkLicenseExpire = () => {

        if (licenseExpire) {

            // run the DB schema to never check again
            UsersApi.SetPagePreference(
                'http://schemas.gmetrix.net/claims/Admin/Preference/PagePreference/ExpiringLicense',
                'Hide'
            );

        }
        setState({ showExpiringLicenseNoticeShow: false });

    };

    const checkCancelStep = () => {

        if (currentAccessCodeStep === 1) {

            setState({ currentAccessCodeStep: 0 });

        } else {

            setState({ newAccessCodeData: undefined, showNewAccessCodeModal: false });

        }

    };

    const handleShowModal = () => setState({ showNewAccessCodeModal: true });

    const noLicensedContent = !(globalLicenses?.licenses.length > 0);
    const managedPartnerTypeName = partnerTypeNames[(selectedPartner?.type ?? 0) - 1] ?? 'Testing Center';

    const showAccessCodeHeader = transactionTitle === formatMessage({ id: 'access_codes.3114ae04', defaultMessage: 'Access Codes' }) && classroomContentTab;

    return (
        <KendoIntlProvider>
            <LocalizationProvider language={locale}>
                <IntlProvider locale={locale}>

                    <div className={'access-codes d-flex flex-column gap-3'}>

                        <ConditionalWrapper
                            wrapper={children => (
                                <PageWithHeader
                                    headerConfig={{
                                        cta: {
                                            className: 'joyride-new-transaction',
                                            disabled: noLicensedContent,
                                            hidden: permissions.Create === false,
                                            label: (
                                                <div className={'d-inline-flex align-items-center gap-1'}>
                                                    <i className={'fa-solid fa-plus'} />
                                                    <span>
                                                        <FormattedMessage defaultMessage="New Access Code" id="new_access_code.becdd264" />
                                                    </span>
                                                </div>
                                            ),
                                            onClick: handleShowModal,
                                            popoverContent: formatMessage({ id: 'this_partnertype_has_no_licensed_content.8db122d0', defaultMessage: 'This {partnerType} has no licensed content available, so an access code cannot be created yet.' }, { partnerType: managedPartnerTypeName }),
                                            renderPopover: noLicensedContent
                                        },
                                        helpContent: helpContent,
                                        title: transactionTitle
                                    }}
                                >
                                    {children}
                                </PageWithHeader>
                            )}
                            condition={showAccessCodeHeader}
                        >

                            {!showAccessCodeHeader && (
                            <div className="mt-4"></div>
                            )}

                            <Card body>
                                <TransactionGMetrixTable
                                    manuallyRenderTransactions
                                    serverSidePagination
                                    toggleColumns
                                    columnData={[
                                        { field: 'Name', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) },

                                        ...(isSystemMode ? [{
                                            cell: ({ dataItem }) => (
                                                <td>
                                                    {(!dataItem.Partner || !dataItem.Partner.Name || dataItem.Partner.Name.trim() === '') ? <FormattedMessage defaultMessage="No Identifier" id="no_identifier.541bd51c" /> : dataItem.Partner.Name}
                                                </td>
                                            ),
                                            field: 'Partner',
                                            sortField: 'Partner',
                                            title: formatMessage({ id: 'partner_name.4cf8979c', defaultMessage: 'Partner Name' })
                                        }] : []),

                                        {

                                            cell: ({ dataItem }) => (
                                                <td>
                                                    {(!dataItem.Identifier || dataItem.Identifier.trim() === '') ? <FormattedMessage defaultMessage="No Identifier" id="no_identifier.541bd51c" /> : dataItem.Identifier}
                                                </td>
                                            ),
                                            field: 'Identifier',
                                            sortField: 'Identifier',
                                            title: formatMessage({ id: 'identifier.0dd31b55', defaultMessage: 'Identifier', description: 'Formulario' })
                                        },
                                        {
                                            field: 'UsesRemaining', title: formatMessage({ id: 'uses_remaining.d26b9e27', defaultMessage: 'Uses Remaining' })
                                        },
                                        {
                                            field: 'NumOfStudents',
                                            sortField: 'NumStudents',
                                            title: formatMessage({ id: 'number_of_students.24d788d2', defaultMessage: 'Number of Students' })
                                        },
                                        {
                                            field: 'Groups',
                                            hidden: true,
                                            title: formatMessage({ id: 'classrooms.e66138fb', defaultMessage: 'Classrooms' })
                                        },
                                        {
                                            cell: ({ dataItem }) => {

                                                const licenses = dataItem.LicensesCSV;
                                                if (!licenses) return <td></td>;

                                                const licensesArray = licenses.split(',').map(l => l.trim());

                                                const displayLimit = 5;
                                                const displayed = licensesArray.slice(0, displayLimit);
                                                const extraCount = licensesArray.length - displayLimit;

                                                return (
                                                    <td>
                                                        {displayed.join('\n')}
                                                        {extraCount > 0 && `\nand ${extraCount} more...`}
                                                    </td>
                                                );

                                            },
                                            field: 'LicensesCSV',
                                            title: formatMessage({ id: 'licenses.11b1c36a', defaultMessage: 'Licenses' }),

                                            width: 200
                                        },
                                        { field: 'CreatedDate', sortField: 'CreateDate', title: formatMessage({ id: 'date_created.85efe6f3', defaultMessage: 'Date Created' }) },
                                        { cell: ({ dataItem }) => (
                                            <td>
                                                {dataItem.ExpirationDateString ? getFormattedDate(dataItem.ExpirationDate) : getFormattedDate(dataItem.LeastOrderExpirationDate)}
                                            </td>
                                        ),
                                        field: 'ExpirationDate',
                                        sortField: 'EndDate',
                                        title: intl.formatMessage({ id: 'end_date.10ec21c1', defaultMessage: 'End Date' }) },
                                        {
                                            field: 'CreatedBy',
                                            title: formatMessage({ id: 'created_by.1ae66f0e', defaultMessage: 'Created By', description: 'Gridview header' })
                                        },
                                        { field: 'Instructor', hidden: true, title: formatMessage({ id: 'instructor.845e6d37', defaultMessage: 'Instructor' }) }
                                    ]}
                                    detailsDropdownItems={dataItem => [
                                        {
                                            action: () => {

                                                if (dataItem.TransactionType === TransactionType.Multiple) {

                                                    navigate('/access-codes/' + dataItem.Id, { state: { subTab: 2, title: dataItem.Name } }); // open the Codes tab

                                                } else {

                                                    copyToClipboard(dataItem.Codes ?? formatMessage({ id: 'no_code_available.1c90a702', defaultMessage: 'No code available' }));
                                                    createToast(formatMessage({ id: 'code_was_copied_to_clipboard.3ca3c48b', defaultMessage: 'Code was copied to clipboard' }), 'success');

                                                }

                                            },
                                            hidden: false,
                                            iconClass: 'fa-cabinet-filing text-warning',
                                            text: dataItem.TransactionType === TransactionType.Multiple
                                                ? formatMessage({ id: 'view_codes.20502e85', defaultMessage: 'View Codes' })
                                                : formatMessage({ id: 'copy_code.b4e097d4', defaultMessage: 'Copy Code' })
                                        },
                                        {
                                            action: () => {

                                                handleDuplicateTransaction(dataItem);

                                            },
                                            iconClass: 'fa-copy text-primary',
                                            text: formatMessage({ id: 'duplicate.9ae890dd', defaultMessage: 'Duplicate' })
                                        },
                                        {
                                            action: async () => {

                                                try {

                                                    const result = await TransactionApi.SetArchivedStatus(dataItem.Id, true);
                                                    if (!result.Done) {

                                                        createToast(result.Message, 'danger');
                                                        return;

                                                    }

                                                    createToast(formatMessage({ id: 'access_code_was_archived_successfully.787657ea', defaultMessage: 'Access Code was archived successfully' }), 'success');

                                                } catch (error: any) {

                                                    createToast(error.Message.toString(), 'danger');

                                                }
                                                fetchTransactionsPaged();

                                            },
                                            hidden: dataItem.Archived,
                                            iconClass: 'fa-cabinet-filing text-warning',
                                            text: formatMessage({ id: 'archive.61ce1f3a', defaultMessage: 'Archive' })
                                        },
                                        {
                                            action: async () => {

                                                try {

                                                    const result = await TransactionApi.SetArchivedStatus(dataItem.Id, false);
                                                    if (!result.Done) {

                                                        createToast(result.Message, 'danger');
                                                        return;

                                                    }

                                                    createToast(formatMessage({ id: 'access_code_was_restored_successfully.526687d8', defaultMessage: 'Access Code was restored successfully' }), 'success');

                                                } catch (error: any) {

                                                    createToast(error.Message.toString(), 'danger');

                                                }
                                                fetchTransactionsPaged();

                                            },
                                            hidden: !dataItem.Archived,
                                            iconClass: 'fa-undo text-info',
                                            text: formatMessage({ id: 'restore.d528bbad', defaultMessage: 'Restore' })
                                        }
                                    ]}
                                    allowExport={false}
                                    currentUserId={userId}
                                    defaultItemsPerPage={paginationTake}
                                    initialSkip={paginationSkip}
                                    key={tableKey}
                                    loadingGridData={loadingGridData}
                                    onDetailsItemClick={async dataItem => navigate('/access-codes/' + dataItem.Id, { state: { from: currentLookupUrl(), title: dataItem.Name } })}
                                    onFilterChange={handleFilterChange}
                                    onSelectedChange={val => setState({ selectedTransactions: val })}
                                    onServerPageChange={handleServerPageChange}
                                    onServerSortChange={handleServerSortChange}
                                    permissions={permissions}
                                    searchTerm={searchTerm}
                                    selectableRows={false}
                                    selectedTransactions={selectedTransactions}
                                    serverTotalCount={serverTotalCount}
                                    setLoadingGridData={val => setState({ loadingGridData: val })}
                                    setSearchTerm={val => setState({ searchTerm: val })}
                                    setTransactions={val => setState({ transactions: val })}
                                    transactions={transactions}
                                />
                            </Card>

                            <GMetrixDialog
                                showCheckBox
                                actionButtonClick={checkLicenseExpire}
                                actionButtonDisabled={false}
                                actionButtonText={formatMessage({ id: 'ok.981b7e92', defaultMessage: 'Ok' })}
                                checkBoxLabel={formatMessage({ id: 'dont_show_me_this_again.db957447', defaultMessage: 'Don\'t show me this again' })}
                                dialogLabel={expiringMessage}
                                onCancel={() => setState({ showExpiringLicenseNoticeShow: false })}
                                setCheckBoxValue={val => setState({ licenseExpire: val })}
                                show={showExpiringLicenseNoticeShow}
                                showCancelButton={false}
                            >
                                <Row className="m-1 mb-3">
                                    {expiringMessageLonger}
                                </Row>
                                <Row className="m-1 table-padding">
                                    <GMetrixTable
                                        columnData={[
                                            { field: 'LicenseName', title: intl.formatMessage({ id: 'license.506a29aa', defaultMessage: 'License' }) },
                                            {
                                                field: 'OrderExpireDateString',
                                                title: intl.formatMessage({ id: 'expiration_date.7e4bebd2', defaultMessage: 'Expiration Date' }),
                                                width: 150
                                            },
                                            {
                                                cell: (props: any) => {

                                                    // eslint-disable-next-line react/prop-types
                                                    const lowestUses = getLowestLicenseAmount(props.dataItem);
                                                    return (
                                                        <td>
                                                            {lowestUses === unlimitedUses ? 'Unlimited' : lowestUses}
                                                        </td>
                                                    );

                                                },
                                                field: 'NumAvailable',
                                                title: intl.formatMessage({ id: 'uses_left.aae1b7d3', defaultMessage: 'Uses Left' }),
                                                width: 125
                                            }
                                        ]}
                                        rowData={licensesExpiring}
                                    ></GMetrixTable>
                                </Row>
                            </GMetrixDialog>
                        </ConditionalWrapper>
                    </div>

                    <Modal
                        onHide={() => setState({ currentAccessCodeStep: 0, newAccessCodeData: undefined, showNewAccessCodeModal: false })}
                        show={showNewAccessCodeModal}
                        size="xl"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title className="w-100">

                                <FormattedMessage defaultMessage="New Access Code" id="new_access_code.becdd264" />
                            </Modal.Title>
                        </Modal.Header>

                        {currentAccessCodeStep === 0 ?
                            (
                                <FindContent
                                    onSuccess={contentData => {

                                        if (contentData !== undefined) {

                                            setState({ currentAccessCodeStep: 1, newAccessCodeData: { ...newAccessCodeData, ...contentData } });

                                        }

                                    }
                                    }
                                    ActionsWrapper={Modal.Footer}
                                    ContentWrapper={Modal.Body}
                                    onClose={() => checkCancelStep()}
                                    selectedContent={newAccessCodeData && (newAccessCodeData as any).content}
                                    variant="picker"
                                />
                            )
                            :
                            (
                                <NewAccessCodeForm
                                    onSuccess={accessCodeId => {

                                        setState({ currentAccessCodeStep: 0, newAccessCodeData: undefined, showNewAccessCodeModal: false });
                                        const url = new URL(window.location.href);
                                        const search_params = url.searchParams;
                                        search_params.delete('create');
                                        url.search = search_params.toString();
                                        window.history.replaceState(null, '', url);
                                        createToast(formatMessage({ id: 'access_code_created_sccessfully.1c1baf84', defaultMessage: 'Access Code created sccessfully' }), 'success');
                                        navigate('/access-codes/' + accessCodeId, { state: { from: currentLookupUrl() } });

                                    }
                                    }
                                    ActionsWrapper={Modal.Footer}
                                    ContentWrapper={Modal.Body}
                                    newAccessCodeData={newAccessCodeData}
                                    onClose={() => checkCancelStep()}
                                />
                            )
                        }

                    </Modal>

                </IntlProvider>
            </LocalizationProvider>
        </KendoIntlProvider>
    );

};
